<!-- 
    季度选择组件
    value/v-model 绑定值
    format 显示在输入框中的值的格式，引入季度：q（阿拉伯数字）、Q（中文数字）YYYY年第Q/q季度
    placeholder 占位内容
    value-format/valueFormat 绑定值的格式，不指定则为Date对象，格式包含YYYYMM、YYYYMm、YYYYmm、YYYYQQ、YYYYqq、YYYYQ、YYYYq
    其中 
    YYYYMM：绑定值为每季度的第一月，例如2023年第一季度：202301，2023年第二季度：202304，2023年第三季度：202307,2023年第四季度：202310
    YYYYMm：绑定值为每季度的中间月，例如2023年第一季度：202302, 2023年第二季度：202305，2023年第三季度：202308,2023年第四季度：202311
    YYYYmm：绑定值为每季度的最后月，例如2023年第一季度：202303, 2023年第二季度：202306，2023年第三季度：202309,2023年第四季度：202312
    YYYYQQ/YYYYqq：绑定值为季度，例如2023年第一季度：202301,2023年第二季度：202302，2023年第三季度：202303,2023年第四季度：202304
    YYYYQ/YYYYq：绑定值为季度，例如2023年第一季度：20231,2023年第二季度：20232，2023年第三季度：20233,2023年第四季度：20234
    使用示例
    import QuarterPicker from '@/components/quarter-picker'
        const range = ref({
            quarter: '202401'
        })
    QuarterPicker ref="quarterValue" format="YYYY年第q季度" value-format="YYYYQQ" :disabled-date="disabledQuarter"
                    @change="handleChangeQuarter" v-model="range.quarter" />
 -->
<template>
  <ElPopover
    ref="quarterPopover"
    :visible="pickerVisible"
    trigger="click"
    popper-class="quarter-popover el-date-picker"
    transition="el-zoom-in-top"
    :width="width"
  >
    <template #reference>
      <ElInput
        class="el-date-editor"
        :prefix-icon="Calendar"
        :clearable="clearable"
        v-model="displayValue"
        :placeholder="placeholder"
        @click="pickerVisible = true"
        @clear="clearModelValue"
        readonly
        style="width: 100%"
      >
      </ElInput>
    </template>
    <div v-click-outside="closePopover" class="el-date-picker">
      <div class="el-date-picker__header el-date-picker__header--bordered">
        <span role="button" class="el-date-picker__prev-btn">
          <button
            type="button"
            :aria-label="$t('commonComponents.previousYear')"
            class="el-picker-panel__icon-btn"
            @click="getPrevYear"
          >
            <ElIcon>
              <DArrowLeft />
            </ElIcon>
          </button>
        </span>
        <span role="button" class="el-date-picker__header-label"
          >{{ `${year} ${$t('commonComponents.year')}` }}</span
        >
        <span role="button" class="el-date-picker__next-btn">
          <button
            type="button"
            :aria-label="$t('commonComponents.nextYear')"
            class="el-picker-panel__icon-btn"
            @click="getNextYear"
          >
            <ElIcon>
              <DArrowRight />
            </ElIcon>
          </button>
        </span>
      </div>
      <div class="el-picker-panel__content" style="margin: 10px 15px">
        <table class="quarter-table" @click="handleTableClick">
          <tbody>
            <tr>
              <td class="available" :class="getCellStyle(0)">
                <a class="cell">{{ $t('commonComponents.firstQuarter') }}</a>
              </td>
              <td class="available" :class="getCellStyle(1)">
                <a class="cell">{{ $t('commonComponents.secondQuarter') }}</a>
              </td>
            </tr>
            <tr>
              <td class="available" :class="getCellStyle(2)">
                <a class="cell">{{ $t('commonComponents.thirdQuarter') }}</a>
              </td>
              <td class="available" :class="getCellStyle(3)">
                <a class="cell">{{ $t('commonComponents.fourthQuarter') }}</a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </ElPopover>
</template>

<script setup>
import {
  ElPopover,
  ElInput,
  ElIcon,
  dayjs,
  ClickOutside as vClickOutside,
} from "element-plus";
import { Calendar, DArrowLeft, DArrowRight } from "@element-plus/icons-vue";
import { computed, ref, watch } from "vue";
import { hasClass } from "element-plus/es/utils/dom/style";
import { $t } from "@/i18n";

const getDayCountOfMonth = function (year, month) {
  if (isNaN(+month)) return 31;
  return new Date(year, +month + 1, 0).getDate();
};

const modifyDate = function (date, y, m, d) {
  return new Date(
    y,
    m,
    d,
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    date.getMilliseconds()
  );
};

const changeYearMonthAndClampDate = function (date, year, month) {
  const monthDate = Math.min(date.getDate(), getDayCountOfMonth(year, month));
  return modifyDate(date, year, month, monthDate);
};

const formatDate = function (date, format) {
  if (!date) return "";
  const result = dayjs(date).format(format);
  return result;
};

const range = function (n) {
  return Array.from({ length: n }).map((_, n) => n);
};

const nextDate = function (date, amount = 1) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + amount);
};

const prevYear = function (date, amount = 1) {
  const year = date.getFullYear();
  const month = date.getMonth();
  return changeYearMonthAndClampDate(date, year - amount, month);
};

const nextYear = function (date, amount = 1) {
  const year = date.getFullYear();
  const month = date.getMonth();
  return changeYearMonthAndClampDate(date, year + amount, month);
};

const props = defineProps({
  format: {
    type: String,
    default: "",
  },
  modelValue: {
    type: String,
    default: "",
  },
  disabled: Boolean,
  valueFormat: {
    type: String,
    default: "",
  },
  width: {
    type: String,
    default: "324px",
  },
  clearable: {
    type: Boolean,
    default: true,
  },
  placeholder: {
    type: String,
    default: () => $t('commonComponents.selectQuarterTip'),
  },
  disabledDate: {
    type: Function,
    default: () => false,
  },
});

const pickerVisible = ref(false);
const date = ref();
const year = computed(() => date.value.getFullYear());
const quarter = ref();
const quarterTextEn = ["First Quarter", "Second Quarter", "Third Quarter", "Fourth Quarter"];
const quarterTextZhCn = ["一", "二", "三", "四"];
const quarterText = computed(() => {
  return $t('common.mode') === "en"
    ? quarterTextEn
    : quarterTextZhCn;
});

const closePopover = () => {
  pickerVisible.value = false;
};

const parsedValue = computed(() => {
  if (!props.modelValue) {
    return new Date();
  }
  if (props.valueFormat) {
    return valueFormatQuarter(props.modelValue);
  }
  return new Date(props.modelValue);
});

const displayValue = computed({
  get: () => {
    if (!props.modelValue) return null;
    const quarter =
      parsedValue.value.getMonth() == 11
        ? 3
        : ~~(parsedValue.value.getMonth() / 3);
    let fDate = formatDate(parsedValue.value, props.format);
    fDate = fDate
      .replace(/Q/, quarter + 1 + "")
      .replace(/q/, quarterText.value[quarter]);
    return fDate;
  },
  set: () => {},
});

watch(
  () => props.modelValue,
  (val) => {
    if (props.valueFormat) {
      date.value = val ? valueFormatQuarter(val) : new Date();
    } else {
      date.value = val ? parsedValue.value : new Date();
    }
  },
  {
    immediate: true,
  }
);

function valueFormatQuarter(date) {
  if (props.valueFormat.includes("Q") || props.valueFormat.includes("q")) {
    const mon = parseInt(date.slice(date.length - 1, date.length)) * 3;
    const str = date.slice(0, 4) + mon;
    return dayjs(str).add(8, "h").toDate();
  } else {
    return dayjs(date).add(8, "h").toDate();
  }
}

const clearModelValue = () => {
  emit("update:modelValue", "");
};

const emit = defineEmits(["update:modelValue"]);

const handleTableClick = (event) => {
  let target = event.target;
  if (target.tagName === "A") {
    target = target.parentNode;
  }
  if (target.tagName !== "TD") return;
  if (hasClass(target, "disabled")) return;
  const column = target.cellIndex;
  const row = target.parentNode.rowIndex;
  const currentQuarter = row * 2 + column;
  const month = currentQuarter * 3;
  let newDate = new Date(year.value, month, 1);
  if (props.valueFormat) {
    if (props.valueFormat.includes("Q") || props.valueFormat.includes("q")) {
      let midStr = props.valueFormat.length == 5 ? "" : "0";
      newDate = year.value + midStr + (currentQuarter + 1);
    } else {
      if (props.valueFormat.includes("MM")) {
        let mon = month + 1;
        newDate =
          (mon + "").length == 2
            ? year.value + "" + mon
            : year.value + "0" + mon;
      } else if (props.valueFormat.includes("Mm")) {
        let mon = month + 2;
        newDate =
          (mon + "").length == 2
            ? year.value + "" + mon
            : year.value + "0" + mon;
      } else if (props.valueFormat.includes("Mm")) {
        let mon = month + 3;
        newDate =
          (mon + "").length == 2
            ? year.value + "" + mon
            : year.value + "0" + mon;
      } else {
        let mon = month + 1;
        newDate =
          (mon + "").length == 2
            ? year.value + "" + mon
            : year.value + "0" + mon;
      }
    }
  }
  quarter.value = currentQuarter + 1;
  emit("update:modelValue", newDate.toString());
  pickerVisible.value = false;
};

const getPrevYear = () => {
  date.value = prevYear(date.value);
};

const getNextYear = () => {
  date.value = nextYear(date.value);
};

const getDayCountOfQuarter = (year, quarter) => {
  switch (quarter) {
    case 0:
      if ((year % 4 === 0 && year % 100 !== 0) || year % 400 === 0) {
        return 91;
      } else {
        return 90;
      }
    case 1:
      return 91;
    default:
      return 92;
  }
};

const datesInYearAndQuarter = (year, quarter) => {
  const numOfDays = getDayCountOfQuarter(year, quarter);
  const firstDay = new Date(year, quarter * 3, 1);
  return range(numOfDays).map((n) => nextDate(firstDay, n));
};

const getCellStyle = (quarter) => {
  const today = new Date();
  const date = parsedValue.value;
  return {
    disabled:
      typeof props.disabledDate === "function"
        ? datesInYearAndQuarter(year.value, quarter).every(props.disabledDate)
        : false,
    current:
      date.getFullYear() === year.value && ~~(date.getMonth() / 3) === quarter,
    quarter:
      today.getFullYear() === year.value &&
      ~~(today.getMonth() / 3) === quarter,
  };
};

defineExpose({
  year,
  quarter,
});
</script>

<style>
.quarter-popover {
  padding: 0 !important;
}

.quarter-table {
  border-collapse: collapse;
  width: 100%;
  margin: -1px;
  font-size: 12px;
}

.quarter-table td {
  padding: 20px 3px;
  text-align: center;
  cursor: pointer;
}

.quarter-table td .cell {
  display: block;
  height: 32px;
  margin: 0 auto;
  color: #606266;
  line-height: 32px;
}

.quarter-table td .cell:hover {
  color: var(--primary-color);
}

.quarter-table td.current:not(.disabled) .cell {
  font-weight: bold;
  color: var(--primary-color);
}

.quarter-table td.disabled .cell {
  color: #a8abb2;
  background-color: #f5f7fa;
  cursor: not-allowed;
}
</style>
